import { useCallback, useSyncExternalStore } from 'react';

/**
 * A tiny router over the History API. The flow is a fixed set of paths and every real navigation
 * (Google SSO, Lemon Squeezy checkout) is a full-page redirect, so a routing library would be
 * dependency weight without a job.
 *
 * The one subtlety that a bare per-component `useState` gets wrong: `pushState` emits no event, so a
 * `navigate()` would only update the `path` of the component that CALLED it. The component that
 * BRANCHES on the path (a different `useRoute` consumer — e.g. the product router vs. a button deep
 * inside it) would keep its own stale copy and never re-render, so the URL would change but the
 * screen would not. The path therefore lives in ONE module-level store that every `useRoute`
 * subscribes to via `useSyncExternalStore`; `navigate` notifies it, and `popstate` covers
 * back/forward.
 */
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  window.addEventListener('popstate', onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('popstate', onStoreChange);
  };
}

function getPathname(): string {
  return window.location.pathname;
}

function getSearch(): string {
  return window.location.search;
}

export interface NavigateOptions {
  /**
   * Replace the current history entry instead of pushing a new one.
   *
   * The distinction is what makes the back button usable in a filter UI. A continuous change —
   * typing in a search box — must REPLACE, or one typed word buries the previous screen under eight
   * history entries and Back stops meaning anything. A discrete act — clicking a facet, changing the
   * sort, turning a page — must PUSH, so that one Back undoes exactly one decision.
   */
  replace?: boolean;
}

/**
 * The current route, and a way to change it.
 *
 * `search` is exposed alongside `path` so view state (filters, sort) can live in the URL rather than
 * in component memory. That is what makes a search shareable, survivable across a refresh, and
 * undoable with the browser's own Back button — which is otherwise the gesture that throws a user
 * out of the app entirely.
 *
 * TWO subscriptions rather than one returning `{path, search}`: `useSyncExternalStore` compares
 * snapshots by identity, so a getter building a fresh object on every call re-renders forever.
 * Strings compare by value, so each is safe on its own.
 */
export function useRoute(): {
  path: string;
  search: string;
  navigate: (to: string, options?: NavigateOptions) => void;
} {
  const path = useSyncExternalStore(subscribe, getPathname, getPathname);
  const search = useSyncExternalStore(subscribe, getSearch, getSearch);

  const navigate = useCallback((to: string, options?: NavigateOptions) => {
    if (options?.replace) window.history.replaceState({}, '', to);
    else window.history.pushState({}, '', to);
    notify();
  }, []);

  return { path, search, navigate };
}

/**
 * Read a token from the URL FRAGMENT and strip it immediately.
 *
 * The fragment is never sent to a server and never lands in access logs or a Referer header — which
 * is exactly why the backend puts magic-link and invite tokens there rather than in a query string.
 * Replacing the history entry stops the token surviving in the back button or a shared URL.
 */
export function takeFragmentToken(): string | null {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const token = new URLSearchParams(hash).get('token');
  if (token) window.history.replaceState({}, '', window.location.pathname);
  return token;
}
